import React, { useEffect, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Marquee } from '../widget-types/rss-feed/RssFeed';
import useStyles from './styles';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import demoData from '../../demoData';
import { DigitalClock } from '../widget-types/clock/Clock';
import { useTheme as useCustomTheme } from '@mui/material';
import { CompactTable } from '@table-library/react-table-library/compact';
import { useTheme } from '@table-library/react-table-library/theme';
import { getTheme } from '@table-library/react-table-library/baseline';
import { useSort } from '@table-library/react-table-library/sort';
import { usePagination } from '@table-library/react-table-library/pagination';

type Props = {
    currentDay?: string;
    currentDemoDate?: string;
    currTemp?: number;
    demoWidgetClockConfig?: any;
    hourMinuteFontSize?: string;
    title?: string;
    imageUrl?: string;
    isClock?: boolean;
    isBarChart?: boolean;
    isDataTable?: boolean;
    isLineChart?: boolean;
    isRss?: boolean;
    isSingleValue?: boolean;
    secondsFontSize?: string;
    youtubeAutoPlay?: boolean;
    widgetWidth?: number;
    widgetHeight?: number;
    youtubeUrl?: string;
};

const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top' as const,
        },
        title: {
            display: false,
            text: 'Chart.js Charts',
        },
    },
    borderStyle: 'solid',
};

const data = {
    labels: ['0', '1', '2', '3', '4', '5', '6'],
    labelsTypeId: 1,
    datasets: [
        {
            label: 'AIGateway.tokenCountRequest',
            data: [3247902, 2438919, 3535771, 2856189, 3534310, 5696586, 4974283],
        },
        {
            label: 'AIGateway.tokenCountCompletion',
            data: [351873, 265295, 388178, 312798, 394261, 621626, 542688],
        },
        {
            label: 'AIGateway.tokenCountTotal',
            data: [3599775, 2704214, 3923949, 3168987, 3928571, 6318212, 5516971],
        },
    ],
};

const DemoPreview = ({
    currentDay,
    currentDemoDate,
    currTemp,
    demoWidgetClockConfig,
    hourMinuteFontSize,
    imageUrl,
    isBarChart,
    isDataTable,
    isClock,
    isLineChart,
    isRss,
    isSingleValue,
    secondsFontSize,
    widgetHeight,
    widgetWidth,
    youtubeAutoPlay,
    youtubeUrl,
}: Props) => {
    const classes = useStyles();
    const [t] = useTranslation();
    const [pictureSizeFactor, setPictureSizeFactor] = useState(1);
    const [fontSize, setFontSize] = useState('1.6rem');
    const rssDemoText = 'DEMO_DATA.WIDGET_TYPE_RSS_TEXT_DEMO';
    const customTheme = useCustomTheme();
    const evenColor = customTheme.palette.neutral[100];
    /*IMPORTANT needs to be registered, otherwise it will break*/
    ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);
    const [userData, setUserData] = useState({
        labels: demoData.map((data) => data.year),
        datasets: [
            {
                /*name that will be shown in the tooltip*/
                label: 'Users gained',
                data: demoData.map((data) => data.userGain),
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
            },
            {
                label: 'Users lost',
                data: demoData.map((data) => data.userLost),
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
            },
        ],
    });

    useEffect(() => {
        if (widgetHeight > 0) {
            setFontSize(widgetHeight < 100 ? '2.5rem' : widgetHeight < 150 ? '3rem' : widgetHeight < 200 ? '4rem' : '5rem');
        }
        if (widgetWidth >= 250 && widgetHeight >= 250) {
            setPictureSizeFactor(4);
        } else if (widgetWidth >= 175 && widgetHeight >= 175) {
            setPictureSizeFactor(3);
        } else if (widgetWidth >= 100 && widgetHeight >= 100) {
            setPictureSizeFactor(2);
        } else if (widgetWidth < 100 || widgetHeight < 100) {
            setPictureSizeFactor(1.5);
        }
        // eslint-disable-next-line
    }, [widgetWidth, widgetHeight]);

    const nodes = data?.labels.map((label, index) => ({
        id: label,
        ...data?.datasets.reduce((acc, dataset) => {
            acc[dataset.label] = dataset.data[index];
            return acc;
        }, {}),
    }));

    const tableData = { nodes };

    const COLUMNS = [
        { label: 'ID', sort: { sortKey: 'id' }, renderCell: (item) => item.id, resize: true },
        ...data?.datasets.map((dataset) => ({
            label: t(`CHART_DATA.${dataset.label.toUpperCase()}`),
            sort: { sortKey: dataset.label },
            renderCell: (item) => item[dataset.label],
            resize: true,
        })),
    ];

    const sortFns: any = data?.datasets.reduce(
        (acc, dataset) => {
            acc[dataset.label] = (array) => array.sort((a, b) => a[dataset.label] - b[dataset.label]);
            return acc;
        },
        {
            id: (array) => array.sort((a, b) => a.id.localeCompare(b.id)),
        },
    );

    const sort = useSort(
        tableData,
        {
            state: { sortKey: 'id', reverse: false },
        },
        {
            sortFns,
        },
    );

    const pagination = usePagination(tableData, {
        state: {
            page: 0,
            size: 1,
        },
    });

    const theme = useTheme([
        getTheme(),
        {
            HeaderRow: `
            background-color: ${customTheme.palette.grey[200]};
            color: ${customTheme.palette.text.primary};

            & th {
                padding: ${customTheme.spacing(2)}px;
            }

        `,
            Row: `
            & td {
                max-height: ${customTheme.spacing(3)}px;
                padding: ${customTheme.spacing(3)}px;
            }
            &:nth-of-type(odd) {
                background-color: ${customTheme?.palette.neutral[50]};
            }
            &:nth-of-type(even) {
                background-color: ${evenColor};
            }
            color: ${customTheme.palette.text.secondary};
        `,
        },
    ]);

    return (
        <div className={classes.demoBanner} data-testid="demo-preview">
            {/* !isRss && <div className={classes.demoTitle} data-testid="demo-title">{t(title)}</div>*/}
            {/*picture*/}
            {imageUrl && <img className={classes.demoImage} src={imageUrl} alt="Demo Image" data-testid="demo-image" />}
            {/*youtube*/}
            {youtubeUrl && (
                <iframe
                    data-testid="demo-youtube"
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${youtubeUrl}?${youtubeAutoPlay ? '&autoplay=1' : ''}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Embedded youtube"
                />
            )}
            {/*weather*/}
            {currTemp && (
                <div data-testid="demo-weather" className={[classes.row, classes.currTemp].join(' ')} style={{ fontSize: fontSize }}>
                    {currTemp}°
                </div>
            )}
            {/*rss*/}
            {isRss && (
                <div
                    data-testid="demo-rss"
                    id="widget-content-height"
                    style={{
                        color: '#000000',
                        width: '100%',
                        height: '100%',
                        fontSize: '24px',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Marquee play={true} pauseOnHover={true} gradientWidth="0">
                        {t(rssDemoText)}
                    </Marquee>
                </div>
            )}
            {/*clock*/}
            {isClock && (
                <div className={classes.root} id="widget-content-height">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {/*date*/}
                        <div className={classes.date} style={{ fontSize: `${secondsFontSize}` }}>
                            <div style={{ paddingRight: '4px' }}>{t(`COMMON.TIME_${currentDay}_TEXT`)}</div>
                            <div style={{ paddingLeft: '4px' }}>{currentDemoDate}</div>
                        </div>
                        {/*clock*/}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                fontSize: `${hourMinuteFontSize}`,
                                fontWeight: 700,
                            }}
                        >
                            <DigitalClock format={'HH:mm'} ticking={true} timezone={demoWidgetClockConfig?.timezone} />
                        </div>
                    </div>
                </div>
            )}
            {isSingleValue && (
                <div data-testid="demo-single-value" className={classes.singleValue}>
                    5
                </div>
            )}
            {/*charts*/}
            {/*line chart*/}
            {isLineChart && <Line data={userData} options={options} />}
            {/*bar chart*/}
            {isBarChart && <Bar data={userData} options={options} />}
            {/*data table*/}
            {isDataTable && <CompactTable theme={theme} columns={COLUMNS} data={tableData} sort={sort} pagination={pagination} />}
        </div>
    );
};

export default DemoPreview;
